import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavDropdown, InputGroup, Form } from "react-bootstrap";
import { COMPARES, GROUPS } from "../../../dates/constants";
import { useDates } from "../../../dates/DatesContext";
import moment from "moment";
import TrellisLink from "../basic/TrellisLink";
import ThemeLine from "../../custom/merchandise/ThemeLine";
import useDashboardTheme, { SIDEBAR_COLORS, type SIDEBAR_SECTION } from "../../../hooks/useDashboardTheme";
import placementIcon from "../../../assets/images/icons/placement-w.png";
import pricingIcon from "../../../assets/images/icons/pricing-w.png";
import productIcon from "../../../assets/images/icons/product-w.png";
import promoIcon from "../../../assets/images/icons/promotion-w.png";
import reportsIcon from "../../../assets/images/icons/marketplace_intelligence.svg";
import { DialogTrigger, Button, Popover, Dialog, Label } from "react-aria-components";
import { Box, Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { css, cx } from "../../../../styled-system/css";
import { ButtonGroup } from "../basic/ButtonGroup";
import { Datepicker } from "../../fields/base/Datepicker";
import { popoverStyles } from "../../fields/styles/styles";
import { cut } from "../../../utils/formatText";
import { useAppSelector } from "../../../redux/hooks";
import { WorkflowProgressV2Banner } from "../../../modules/workflow-progress-v2/components/WorkflowProgressV2Banner";
import { Checkbox } from "../../fields/base/Checkbox";
import { Select } from "../../fields/base/Select";
import type { TSelector } from "../../../i18n/types";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

const Icon = ({ section }: { section: SIDEBAR_SECTION }) => {
  let color = "#523673";
  let iconURL = placementIcon;

  if (section === "placement") {
    color = SIDEBAR_COLORS[section];
    iconURL = placementIcon;
  }
  if (section === "pricing") {
    color = SIDEBAR_COLORS[section];
    iconURL = pricingIcon;
  }

  if (section === "promotions") {
    color = SIDEBAR_COLORS[section];
    iconURL = promoIcon;
  }

  if (section === "product") {
    color = SIDEBAR_COLORS[section];
    iconURL = productIcon;
  }

  if (section === "reports") {
    iconURL = reportsIcon;
  }

  return (
    <div
      style={{
        width: "30px",
        height: "30px",
        backgroundColor: color,
        borderRadius: "100%",
      }}
      className="d-flex align-items-center justify-content-center me-3"
    >
      {iconURL && (
        <img
          src={iconURL}
          style={{
            width: "25px",
            height: "25px",
            borderRadius: "100%",
            // Changes svg color
            color: "#FFF",
          }}
          alt=""
        />
      )}
    </div>
  );
};

type BreadcrumbLabel = string | TSelector;

export interface Breadcrumb {
  searchable?: boolean;
  dropdownLinkOptions?: {
    name: BreadcrumbLabel;
    href?: string;
    subTitle?: BreadcrumbLabel;
    altDisplayMarkup?: React.ReactNode;
  }[];
  name: BreadcrumbLabel;
  href?: string;
  searchPlaceholderText?: string;
  dropdownSubtitle?: string;
  unsorted?: boolean;
  activeOption?: string;
  default?: boolean;
  defaultHref?: string;
  defaultName?: string;
  defaultsubTitle?: string;
}

interface Props {
  items: Breadcrumb[];
  hideCompare?: boolean;
  hideRange?: boolean;
  children?: React.ReactNode;
  groupByOnly?: boolean;
  hideDateSelector?: boolean;
  hideGroupBy?: boolean;
  hideIncludeCurrentPeriod?: boolean;
}

const BreadcrumbDates = ({
  items,
  hideCompare = false,
  hideRange = false,
  children,
  groupByOnly,
  hideDateSelector,
  hideGroupBy = false,
  hideIncludeCurrentPeriod = false,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const [search, setSearch] = useState("");

  const { section } = useDashboardTheme();

  const resolveLabel = (label: BreadcrumbLabel | undefined): string => {
    if (label == null) return "";
    if (typeof label === "function") return t(label);
    return label;
  };

  const MAX_BREADCRUMB_DROPDOWN_ITEMS = 200;

  return (
    <Box>
      <Flex
        className={css({
          ".container-fluid &": { marginX: "-7.5px" },
        })}
        justifyContent={"space-between"}
        borderBottom={"1px solid #dcdadd"}
        paddingX={"xlarge"}
        backgroundColor={"nav"}
      >
        <ol
          role="navigation"
          aria-label="breadcrumbs"
          className="breadcrumb d-flex align-items-center"
          style={{
            marginBottom: "0px",
            fontSize: "14px",
            margin: "revert",
          }}
        >
          <Icon section={section} />
          {channel !== "shopify" &&
            items.map((item, i) => {
              if (item.default && search.length === 0) {
                if (item.dropdownLinkOptions && resolveLabel(item.dropdownLinkOptions[0]?.name) !== item.defaultName) {
                  item.dropdownLinkOptions.unshift({
                    name: item.defaultName!,
                    href: item.defaultHref!,
                    subTitle: item.defaultsubTitle ? item.defaultsubTitle : "",
                  });
                }
              }
              return (
                <li key={i} className="breadcrumb-item ps-2 fw-normal">
                  {item.dropdownLinkOptions ?
                    <NavDropdown
                      key={i}
                      id="DateBreadcrumbComparison"
                      renderMenuOnMount={true}
                      // TODO: Maybe below is better left out so search is persistent - get feedback of preference
                      onMouseLeave={() => setSearch("")}
                      className="fs-standard noarrow dropdown mx-0"
                      title={
                        <div style={{ whiteSpace: "normal", lineHeight: "12px" }}>
                          <TrellisLink className="text-decoration-none " to={item.href ?? "#"}>
                            {cut(item.default && !item.name ? item.defaultName : resolveLabel(item.name), 100)}
                          </TrellisLink>
                        </div>
                      }
                    >
                      {item.searchable && (
                        <div className="px-3 py-2">
                          <InputGroup className="mb-3">
                            <InputGroup.Text
                              style={{
                                borderTopLeftRadius: "45px",
                                borderBottomLeftRadius: "45px",
                              }}
                            >
                              <i className="fa fa-search" />
                            </InputGroup.Text>
                            <Form.Control
                              placeholder={
                                item.searchPlaceholderText ?
                                  item.searchPlaceholderText
                                : t(($) => $.common.searchBar["Search..."])
                              }
                              aria-label={t(($) => $.common.breadcrumbDates["Search Term"])}
                              onChange={(e) => {
                                setSearch(e.target.value);
                              }}
                              value={search}
                            />
                          </InputGroup>
                        </div>
                      )}
                      {item.dropdownSubtitle && (
                        <h3
                          className="m-0 p-2 px-3 fs-3"
                          style={{
                            color: "#2e0054",
                            fontWeight: 600,
                          }}
                        >
                          {item.dropdownSubtitle}
                        </h3>
                      )}
                      <div
                        style={{
                          width: "300px",
                          maxHeight: "500px",
                          overflowY: "auto",
                        }}
                      >
                        {item.dropdownLinkOptions
                          .map((nestedLink) => ({ ...nestedLink, _resolvedName: resolveLabel(nestedLink.name) }))
                          .filter((nestedLink) => {
                            if (search) {
                              return `${nestedLink._resolvedName} ${nestedLink.href}`
                                .toLowerCase()
                                .includes(search.toLowerCase());
                            }

                            return true;
                          })
                          .sort((a, b) => {
                            if (item.unsorted) {
                              return 0;
                            }

                            // Always keep "All Categories" at the top
                            if (a._resolvedName === "All Categories") return -1;
                            if (b._resolvedName === "All Categories") return 1;

                            return (
                              a._resolvedName.toLowerCase() < b._resolvedName.toLowerCase() ? -1
                              : a._resolvedName.toLowerCase() > b._resolvedName.toLowerCase() ? 1
                              : 0
                            );
                          })
                          .splice(0, MAX_BREADCRUMB_DROPDOWN_ITEMS)
                          .map((nestedLink) => (
                            <div
                              className="d-flex flex-column py-2"
                              key={`${nestedLink._resolvedName}-${nestedLink.href ?? "#"}`}
                            >
                              <TrellisLink
                                className={`text-decoration-none px-3`}
                                to={nestedLink.href ?? "#"}
                                style={{
                                  fontWeight:
                                    (
                                      window.location.href.includes(nestedLink.href!) ||
                                      nestedLink._resolvedName === item.activeOption
                                    ) ?
                                      600
                                    : "inherit",
                                }}
                              >
                                <span>
                                  {nestedLink.altDisplayMarkup ? nestedLink.altDisplayMarkup : nestedLink._resolvedName}
                                </span>
                                {nestedLink.subTitle ?
                                  <ThemeLine additionalStyles={{ fontSize: "10px" }}>
                                    {resolveLabel(nestedLink.subTitle)}
                                  </ThemeLine>
                                : ""}
                              </TrellisLink>
                            </div>
                          ))}
                      </div>
                    </NavDropdown>
                  : <TrellisLink className="text-decoration-none " to={item.href!}>
                      {resolveLabel(item.name)}
                    </TrellisLink>
                  }
                </li>
              );
            })}
        </ol>
        <HStack>
          {!hideDateSelector && (
            <DateSelector
              hideCompare={hideCompare}
              hideGroupBy={hideGroupBy}
              hideRange={hideRange}
              groupByOnly={groupByOnly}
              hideIncludeCurrentPeriod={hideIncludeCurrentPeriod}
            />
          )}
          {children}
        </HStack>
      </Flex>
      <WorkflowProgressV2Banner />
    </Box>
  );
};

export default BreadcrumbDates;

interface DateSelectorProps {
  hideCompare?: boolean;
  hideRange?: boolean;
  groupByOnly?: boolean;
  hideGroupBy?: boolean;
  hideIncludeCurrentPeriod?: boolean;
}

const GROUP_NAME_KEY: Record<string, "Day" | "Week" | "Month"> = {
  day: "Day",
  week: "Week",
  month: "Month",
};

const COMPARE_NAME_KEY: Record<string, "Previous Period" | "Previous Year" | "Custom"> = {
  mom: "Previous Period",
  yoy: "Previous Year",
  custom: "Custom",
};

const PERIOD_NAME_KEY: Record<
  string,
  | "Last Month"
  | "Last Week"
  | "Last 7 days"
  | "Last 14 days"
  | "Last 30 days"
  | "Month to Date"
  | "Last 12 weeks"
  | "Last 26 weeks"
  | "Last 6 months"
  | "Last 12 months"
  | "Custom"
> = {
  lastMonth: "Last Month",
  lastWeek: "Last Week",
  last7: "Last 7 days",
  last14: "Last 14 days",
  last30: "Last 30 days",
  month: "Month to Date",
  last12Weeks: "Last 12 weeks",
  last26Weeks: "Last 26 weeks",
  last6Months: "Last 6 months",
  last12Months: "Last 12 months",
  custom: "Custom",
};

function DateSelector({
  hideCompare = false,
  hideGroupBy = false,
  hideIncludeCurrentPeriod = false,
  groupByOnly = false,
  hideRange = false,
}: DateSelectorProps) {
  const { t } = useTranslation(["common"]);
  const dateFormatter = useDateFormatter("short");
  const {
    period,
    compare,
    group,
    setGroup,
    setPeriod,
    setCompare,
    end,
    start,
    preStart,
    preEnd,
    setStart,
    setEnd,
    setPreStart,
    setPreEnd,
    includeCurrentPeriod,
    setIncludeCurrentPeriod,
  } = useDates();
  const selectedGroup = GROUPS.find((g) => g.id === group)!;

  return (
    <DialogTrigger>
      <Button
        className={css({
          all: "unset",
          cursor: "pointer",
          display: "flex",
          gap: "small",
          color: "royalPurple",
          lineHeight: "12px",
          _after: {
            alignSelf: "center",
            borderLeft: "4px solid transparent",
            borderRight: "4px solid transparent",
            borderTop: "4px solid",
            borderBottom: 0,
            width: 0,
            height: 0,
            content: '""',
          },

          _focusVisible: {
            outline: "1px auto {colors.bootstrapBlue}",
            outlineOffset: "1px",
          },
        })}
      >
        <VStack gap="0" justifyContent={"center"} alignItems={"start"}>
          <strong className={css({ fontSize: "medium" })}>
            {dateFormatter.format(start.toDate())} - {dateFormatter.format(end.toDate())}
          </strong>
          <small>
            {t(($) => $.common.breadcrumbDates["by {group}"], {
              group: t(($) => $.common.breadcrumbDates.groupNames[GROUP_NAME_KEY[group] ?? "Day"]),
            })}
            {hideCompare ? null : (
              <span>
                {` ${t(($) => $.common.breadcrumbDates["vs. {compare}"], {
                  compare: t(
                    ($) => $.common.breadcrumbDates.compareNames[COMPARE_NAME_KEY[compare] ?? "Previous Period"],
                  ),
                })}`}
              </span>
            )}
          </small>
        </VStack>
      </Button>
      <Popover>
        <Dialog
          className={cx(
            popoverStyles,
            css({
              gap: "small",
              width: "380px",
              padding: "medium",
            }),
          )}
        >
          {!hideGroupBy && (
            <HStack justifyContent={"space-between"}>
              <Label className={css({ fontSize: "medium" })}>{t(($) => $.common.breadcrumbDates["Group by:"])}</Label>
              <ButtonGroup
                buttons={GROUPS.map((g) => ({
                  label: t(($) => $.common.breadcrumbDates.groupNames[GROUP_NAME_KEY[g.id] ?? "Day"]),
                  value: g.id,
                }))}
                value={group}
                onSelect={setGroup}
                colorPalette={"buttonGroup.fuchsia"}
              />
            </HStack>
          )}
          {!groupByOnly && (
            <>
              {!hideRange && (
                <>
                  <Label className={css({ fontSize: "medium" })}>{t(($) => $.common.breadcrumbDates["Range:"])}</Label>
                  <Select
                    selectedKey={period}
                    items={selectedGroup.periods.map((p) => ({
                      label: t(($) => $.common.breadcrumbDates.periodNames[PERIOD_NAME_KEY[p.id] ?? "Custom"]),
                      value: p.id,
                    }))}
                    onSelectionChange={(value) => setPeriod(value as string)}
                  />
                  <HStack justifyContent={"space-between"}>
                    <Datepicker
                      disabled={period !== "custom"}
                      label={t(($) => $.common.breadcrumbDates["Start"])}
                      value={start.toISOString()}
                      onChange={(d) => setStart(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                    <Datepicker
                      disabled={period !== "custom"}
                      label={t(($) => $.common.breadcrumbDates["End"])}
                      value={end.toISOString()}
                      onChange={(d) => setEnd(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                  </HStack>
                </>
              )}
              {!hideCompare && (
                <>
                  <Label className={css({ fontSize: "medium" })}>
                    {t(($) => $.common.breadcrumbDates["Compare against:"])}
                  </Label>
                  <Select
                    selectedKey={compare}
                    items={COMPARES.map((c) => ({
                      label: t(
                        ($) => $.common.breadcrumbDates.compareNames[COMPARE_NAME_KEY[c.id] ?? "Previous Period"],
                      ),
                      value: c.id,
                    }))}
                    onSelectionChange={(value) => setCompare(value as string)}
                  />
                  <HStack justifyContent={"space-between"}>
                    <Datepicker
                      disabled={compare !== "custom"}
                      label={t(($) => $.common.breadcrumbDates["Start"])}
                      value={preStart.toISOString()}
                      onChange={(d) => setPreStart(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                    <Datepicker
                      disabled={compare !== "custom"}
                      label={t(($) => $.common.breadcrumbDates["End"])}
                      value={preEnd.toISOString()}
                      onChange={(d) => setPreEnd(moment(d ?? undefined))}
                      maxDate={moment().toDate()}
                    />
                  </HStack>
                </>
              )}
              {!hideIncludeCurrentPeriod && (
                <Checkbox
                  value={includeCurrentPeriod}
                  onChange={(value) => setIncludeCurrentPeriod(value)}
                  label={t(($) => $.common.breadcrumbDates["Include current {group}"], {
                    group: t(($) => $.common.breadcrumbDates.groupNames[GROUP_NAME_KEY[group] ?? "Day"]),
                  })}
                  className={css({ marginY: "medium" })}
                />
              )}
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
